/* Pieces of the policy draft -> validate -> simulate -> diff -> publish flow
   (design eval F18). Kept out of pages/Policies.tsx so the page stays a page. */
import { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { clsx } from 'clsx';
import { Banner, Button, Input, Modal, Textarea } from '@/components/ds-zta';
import { lineDiff, diffStats, withContext } from './lineDiff';
import type { RegoIssue } from './regoCheck';
import { evaluateAcl, parseIP } from './cidr';
import { SAMPLE_INPUT_PLACEHOLDER, sampleInputError, typedMatches } from './reviewChecks';

/* ─── Rego issues ─────────────────────────────────────── */
export function RegoIssueList({ issues, id }: { issues: RegoIssue[]; id?: string }) {
    if (issues.length === 0) {
        return (
            <p id={id} className="flex items-center gap-1.5 text-xs text-[var(--d2d-success)]">
                <CheckCircle className="h-3.5 w-3.5" aria-hidden />
                Structure checks pass. These run in your browser and are not a Rego compile; the server can still reject it.
            </p>
        );
    }
    return (
        <ul id={id} className="space-y-1 text-xs" aria-label="Rego checks">
            {issues.map((issue, i) => (
                <li
                    key={i}
                    className={clsx(
                        'flex items-start gap-1.5',
                        issue.severity === 'error' ? 'text-[var(--danger-text)]' : 'text-[var(--d2d-warning)]'
                    )}
                >
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" aria-hidden />
                    <span>
                        <span className="font-mono tabular-nums">
                            Line {issue.line}{issue.column ? `:${issue.column}` : ''}
                        </span>
                        {' · '}
                        <span className="sr-only">{issue.severity}: </span>
                        {issue.message}
                    </span>
                </li>
            ))}
        </ul>
    );
}

/* ─── Field changes ───────────────────────────────────── */
export interface FieldChange {
    field: string;
    before: string;
    after: string;
}

export function FieldChanges({ changes }: { changes: FieldChange[] }) {
    if (changes.length === 0) return null;
    return (
        <dl className="divide-y divide-[var(--d2d-border)] rounded-xl border border-[var(--d2d-border)] text-sm">
            {changes.map((c) => (
                <div key={c.field} className="grid grid-cols-[8rem_1fr] gap-3 px-3 py-2">
                    <dt className="text-[var(--d2d-text-muted)]">{c.field}</dt>
                    <dd className="min-w-0 space-y-0.5 font-mono text-xs break-all">
                        <div className="text-[var(--danger-text)]"><span aria-hidden>- </span><span className="sr-only">Before: </span>{c.before || '(empty)'}</div>
                        <div className="text-[var(--d2d-success)]"><span aria-hidden>+ </span><span className="sr-only">After: </span>{c.after || '(empty)'}</div>
                    </dd>
                </div>
            ))}
        </dl>
    );
}

/* ─── Line diff ───────────────────────────────────────── */
export function RegoDiff({ before, after }: { before: string; after: string }) {
    const diff = useMemo(() => lineDiff(before, after), [before, after]);
    const { added, removed } = diffStats(diff);
    const rows = useMemo(() => withContext(diff, 3), [diff]);
    if (added === 0 && removed === 0) {
        return <p className="text-xs text-[var(--d2d-text-muted)]">Rego unchanged.</p>;
    }
    return (
        <div>
            <p className="mb-1.5 text-xs text-[var(--d2d-text-muted)] tabular-nums">
                <span className="text-[var(--d2d-success)]">+{added}</span>{' '}
                <span className="text-[var(--danger-text)]">-{removed}</span> lines
            </p>
            <div
                role="group"
                aria-label={`Rego diff: ${added} lines added, ${removed} removed`}
                className="max-h-72 overflow-auto rounded-xl border border-[var(--d2d-border)] font-mono text-xs"
            >
                {rows.map((row, i) => {
                    if ('hidden' in row) {
                        return (
                            <div key={i} className="px-3 py-0.5 text-[var(--d2d-text-muted)] bg-[var(--d2d-bg-secondary)] italic">
                                {row.hidden} unchanged line{row.hidden === 1 ? '' : 's'}
                            </div>
                        );
                    }
                    return (
                        <div
                            key={i}
                            className={clsx(
                                'grid grid-cols-[2.5rem_2.5rem_1rem_1fr] whitespace-pre',
                                row.op === 'add' && 'bg-[var(--zta-tint-success)] text-[var(--d2d-success)]',
                                row.op === 'del' && 'bg-[var(--zta-tint-danger)] text-[var(--danger-text)]',
                                row.op === 'same' && 'text-[var(--d2d-text-secondary)]'
                            )}
                        >
                            <span className="pr-2 text-right text-[var(--d2d-text-muted)] tabular-nums select-none">{row.oldLine ?? ''}</span>
                            <span className="pr-2 text-right text-[var(--d2d-text-muted)] tabular-nums select-none">{row.newLine ?? ''}</span>
                            <span aria-hidden className="select-none">{row.op === 'add' ? '+' : row.op === 'del' ? '-' : ' '}</span>
                            <span>
                                {row.op !== 'same' && <span className="sr-only">{row.op === 'add' ? 'Added: ' : 'Removed: '}</span>}
                                {row.text || ' '}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/* ─── Typed confirmation ──────────────────────────────── */
/** Confirm by typing the policy's name. ZTA's DialogProvider confirmation has no
    typed mode, so this is local to the policies flow. */
export function TypedConfirmField({
    expected,
    value,
    onChange,
    action,
}: {
    expected: string;
    value: string;
    onChange: (v: string) => void;
    action: string;
}) {
    return (
        <Input
            label={<>Type <span className="font-mono">{expected}</span> to {action}</>}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            className="[&_input]:font-mono"
        />
    );
}

export function TypedConfirmModal({
    open,
    title,
    expected,
    action,
    confirmLabel,
    blocked,
    onResult,
    children,
}: {
    open: boolean;
    title: string;
    expected: string;
    /** "publish it", completes "Type <name> to …". */
    action: string;
    confirmLabel: string;
    /** When set, the confirm button stays disabled whatever is typed. */
    blocked?: boolean;
    onResult: (ok: boolean) => void;
    children?: React.ReactNode;
}) {
    const [typed, setTyped] = useState('');
    // Each opening starts empty: reset during render when `open` turns true,
    // React's "adjust state when a prop changes" pattern.
    const [wasOpen, setWasOpen] = useState(open);
    if (open !== wasOpen) {
        setWasOpen(open);
        if (open) setTyped('');
    }
    const ok = typedMatches(expected, typed) && !blocked;
    return (
        <Modal
            open={open}
            onClose={() => onResult(false)}
            dismissOnOverlay={false}
            title={title}
            size="md"
            footer={
                <>
                    <Button variant="secondary" onClick={() => onResult(false)}>Cancel</Button>
                    <Button variant="primary" disabled={!ok} onClick={() => onResult(true)}>{confirmLabel}</Button>
                </>
            }
        >
            <div className="space-y-4">
                {children}
                <TypedConfirmField expected={expected} value={typed} onChange={setTyped} action={action} />
            </div>
        </Modal>
    );
}

/* ─── Simulation ──────────────────────────────────────── */
export function RegoSimulatePanel({ value, onChange }: { value: string; onChange: (v: string) => void }) {
    const err = sampleInputError(value);
    return (
        <div className="space-y-3">
            <Banner variant="info" title="Simulation needs the policy server">
                This API has no evaluate or simulate endpoint, so nothing is evaluated in the browser. Write a sample input
                here: it is checked as JSON and saved with the policy (metadata.sample_input) so it can be run once the
                server offers evaluation. The field names below are an example, not the server's documented input.
            </Banner>
            <Textarea
                label="Sample input (JSON)"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={SAMPLE_INPUT_PLACEHOLDER}
                rows={6}
                error={err ?? undefined}
                className="[&_textarea]:font-mono"
                spellCheck={false}
            />
        </div>
    );
}

export function AclSimulatePanel({ allow, deny }: { allow: string[]; deny: string[] }) {
    const [ip, setIp] = useState('');
    const parsed = ip.trim() ? parseIP(ip) : null;
    const verdict = parsed ? evaluateAcl(parsed, allow, deny) : null;
    let text = '';
    if (verdict) {
        switch (verdict.reason) {
            case 'denied': text = `Denied by ${verdict.rule}.`; break;
            case 'allowed': text = `Allowed by ${verdict.rule}.`; break;
            case 'no-allow-list': text = 'Allowed: the allow list is empty, so every IP that is not denied is allowed.'; break;
            case 'not-allowed': text = 'Denied: it matches no allow entry.'; break;
        }
    }
    return (
        <div className="space-y-2">
            <Input
                label="Test an IP against these lists"
                value={ip}
                onChange={(e) => setIp(e.target.value)}
                placeholder="e.g. 10.0.0.5 or 2001:db8::1"
                className="[&_input]:font-mono"
                error={ip.trim() && !parsed ? 'Not a valid IPv4 or IPv6 address.' : undefined}
                help="Checked in your browser with the rules this page describes (deny first, then allow). The server's evaluation is authoritative."
            />
            {verdict && (
                <p
                    aria-live="polite"
                    className={clsx(
                        'flex items-center gap-1.5 text-sm',
                        verdict.decision === 'allow' ? 'text-[var(--d2d-success)]' : 'text-[var(--danger-text)]'
                    )}
                >
                    {verdict.decision === 'allow' ? <CheckCircle className="h-4 w-4" aria-hidden /> : <AlertTriangle className="h-4 w-4" aria-hidden />}
                    {text}
                </p>
            )}
        </div>
    );
}

/* ─── Version / history ───────────────────────────────── */
export function HistoryNote() {
    return (
        <p className="flex items-start gap-1.5 text-xs text-[var(--d2d-text-muted)]">
            <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" aria-hidden />
            Version history isn't available: the API returns only the current version of a policy (no versions endpoint),
            so earlier versions can't be listed or restored here.
        </p>
    );
}
